
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { ChurchProfileSection } from "./ChurchProfileSection";
import { AdminActions } from "@/components/settings/AdminActions";
import { useChurchData } from "./useChurchData";
import { useToast } from "@/hooks/use-toast";
import { useChurchBranding } from "@/contexts/ChurchBrandingContext";

interface AdminSectionProps {
  isAdmin: boolean;
}

export function AdminSection({ isAdmin }: AdminSectionProps) {
  const session = useSession();
  const { toast } = useToast();
  const [adminStatus, setAdminStatus] = useState<boolean>(isAdmin);
  const { branding, refreshBranding } = useChurchBranding();
  const { 
    congregationName, 
    setCongregationName, 
    logo, 
    setLogo, 
    churchCode,
    setChurchCode,
    isReadOnly, 
    fetchCongregationInfo,
    saveChurchData
  } = useChurchData();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAdminStatus(isAdmin);

    // If admin, fetch congregation info
    if (isAdmin) {
      fetchCongregationInfo();
    }
  }, [isAdmin, fetchCongregationInfo]);

  const handleSaveChurchData = async () => {
    setIsSaving(true);
    try {
      console.log("Saving church data with values:", { 
        congregationName, 
        logo, 
        churchCode 
      });
      
      const success = await saveChurchData();
      
      if (success) {
        console.log("Church data saved successfully, refreshing branding context");
        // Refresh the branding context after saving
        await refreshBranding();
        
        // Show success message
        toast({
          title: "Settings Saved",
          description: "Your church branding has been updated and will appear on all pages.",
        });
      } else {
        console.error("Failed to save church data");
      }
      
      return success;
    } catch (error) {
      console.error("Error saving church data:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // If not an admin, don't show anything
  if (!adminStatus) {
    return null;
  }

  return (
    <div className="space-y-6 pb-6 border-b">
      <h2 className="text-xl font-semibold text-red-600">
        Church Administration
      </h2>
      
      <ChurchProfileSection 
        congregationName={congregationName}
        logo={logo}
        churchCode={churchCode}
        isReadOnly={isReadOnly || isSaving}
        onNameChange={setCongregationName}
        onLogoChange={setLogo}
        onSave={handleSaveChurchData}
      />
      
      <AdminActions />
      
      <p className="text-sm text-app-text-secondary mt-2">
        You have administrator access to manage church resources and member data. 
        The church logo you upload here will be displayed on all pages for members who scan your church's bracelets.
      </p>
    </div>
  );
}
